import { __decorate, __metadata } from 'tslib';
import { Service } from '../../decorators/class/service.js';
import SchematicUIService from '../../controllers/base/schematic-ui-service.js';
import { StateManager } from '../store/state-manager.js';
import { BuiltinUiState } from '../store/builtin-state.js';
import { ServiceNames } from '../../constants/service-name.js';
import { generateRandomId } from '../../utils/string-utils.js';
import { TransitionService } from '../transition/transition-service.js';
import { WorkbenchConfigurationState } from '../settings/auto-sync-settings.js';
import { ShrinkType } from './toolbar-schema.interface.js';
import { DEFAULT_SHELTER_ICON } from '../../components/toolbar/constants/default-shelter-icon.js';
import { getShelterDropdown } from '../../components/toolbar/components/shelter-dropdown.js';

/**
 * 用于管理工具栏按钮的服务
 *
 * @public
 */
let ToolbarService = class ToolbarService extends SchematicUIService {
    /**
     * @internal
     */
    constructor(store, transitionService) {
        super();
        this.store = store;
        this.transitionService = transitionService;
        this.force = 0;
        this.currentPath = [];
        this.readGroup = (groups, schema, index) => {
            this.currentPath.push(`[${index}]`);
            const groupTailShelter = [];
            const customTailShelters = schema.items.map((item) => {
                if (item.shrinkType === ShrinkType.GROUP_TAIL && item.shelter) {
                    return this.createTailShelter(item, schema);
                }
                if (item.shrinkType === ShrinkType.GROUP_TAIL && groupTailShelter.length === 0) {
                    const shelter = this.createTailShelter(item, schema);
                    groupTailShelter.push(shelter);
                }
                return null;
            }).filter(value => !!value);
            const shelterConfig = schema.shelter ? this.get(schema.shelter) : null;
            if (shelterConfig) {
                this.setPathToActionInfoInConfig(shelterConfig, [...this.currentPath]);
            }
            const groupConfig = {
                key: schema.key,
                shelter: shelterConfig,
                items: schema.items
                    .reduce(this.readVirtualGroup, [])
                    .concat(customTailShelters)
                    .concat(groupTailShelter),
            };
            groups.push(groupConfig);
            this.currentPath.pop();
            return groups;
        };
        this.readVirtualGroup = (groups, item) => {
            var _a;
            const { key, shrinkType } = item;
            const shelterConfig = item.shelter ? this.get(item.shelter) : null;
            if (shelterConfig) {
                this.setPathToActionInfoInConfig(shelterConfig, [...this.currentPath]);
            }
            groups.push({
                key,
                shrinkType,
                priority: (_a = item.priority) !== null && _a !== void 0 ? _a : 999,
                shelter: shelterConfig,
                forceShrink: item.forceShrink,
                buttons: item.buttons.reduce(this.readRows, []),
            });
            return groups;
        };
        this.readRows = (rows, row) => {
            const buttons = row.map((button) => {
                const config = this.get(button);
                this.setPathToActionInfoInConfig(config, [...this.currentPath]);
                return config;
            });
            rows.push(buttons);
            return rows;
        };
        this.store.register(BuiltinUiState.FixToolbarShow, true);
        this.store.register(BuiltinUiState.RibbonActiveTab, '');
        this.store.register(BuiltinUiState.ToolbarEvent, { target: 'none', type: 'dropdownClose' });
        this.store.register(BuiltinUiState.ToolbarDropdownEvent, { target: 'none', type: 'dropdownClose' });
        this.store.register(BuiltinUiState.isFocusedByClick, true);
        this.store.register(WorkbenchConfigurationState.DisableToolbar, false).subscribe((value) => {
            this.store.upsert(BuiltinUiState.FixToolbarDOMVisible, !value);
        });
        this.schema = {};
        const takeUpSpaceHeight = this.getTakeUpSpaceHeight();
        this.transitionService.register('fixed-toolbar', {
            duration: 150,
            entered: {},
            entering: {
                transform: 'translateY(0)',
                transition: 'all linear 0.15s',
                width: '100%',
            },
            exited: {
                transition: 'all linear 0.15s',
                position: 'fixed',
                width: '100%',
                transform: `translateY(calc(-100% - ${takeUpSpaceHeight}px))`,
                height: takeUpSpaceHeight,
            },
            exiting: {
                transform: `translateY(calc(-100% - ${takeUpSpaceHeight}px))`,
                transition: 'all linear 0.15s',
                height: takeUpSpaceHeight,
            },
        });
    }
    onUpdate() {
        super.onUpdate();
        Object.entries(this.schema).forEach(([key, toolbarSchemaItem]) => {
            this.currentPath.push(key);
            const toolbarData = {
                label: toolbarSchemaItem.label,
                data: toolbarSchemaItem.data.reduce(this.readGroup, []),
                moreText: toolbarSchemaItem.moreText,
                moreTooltip: toolbarSchemaItem.moreTooltip,
                keyItem: this.get(key),
                tooltip: toolbarSchemaItem.tooltip,
            };
            this.currentPath.pop();
            this.store.upsert(key, toolbarData);
        });
    }
    /**
     * 更改工具栏顺序配置
     *
     * @param schema - 配置对象
     */
    setSchema(schema) {
        Object.entries(schema).forEach(([, toolbarSchemaItem]) => {
            toolbarSchemaItem.data = this.normalizeGroup(toolbarSchemaItem.data);
        });
        super.setSchema(schema);
    }
    mergeSchemaWithoutUpdate(schema) {
        Object.entries(schema).forEach(([key, value]) => {
            var _a;
            this.schema[key] = Object.assign(Object.assign(Object.assign({}, ((_a = this.schema[key]) !== null && _a !== void 0 ? _a : {})), value), { data: this.schema[key]
                    ? this.schema[key].data.concat(this.normalizeGroup(value.data))
                    : this.normalizeGroup(value.data) });
        });
    }
    /**
     * 标准化用户定义的 schema 到 group 到 virtualGroup 到 item 的形式
     */
    normalizeGroup(data) {
        return data.map((group, index) => {
            const gruopKey = `TG_${index}_${generateRandomId()}`;
            const groupConfig = {
                key: gruopKey,
                items: [],
            };
            if (Array.isArray(group)) {
                // 简写的 schema 形态，进行补全
                groupConfig.items = this.normalizeVirtualGroup(group);
            }
            else {
                groupConfig.shelter = group.shelter;
                groupConfig.items = this.normalizeVirtualGroup(group.items);
            }
            return groupConfig;
        });
    }
    normalizeVirtualGroup(data) {
        return data.map((group, index) => {
            const gruopKey = `VG_${index}_${generateRandomId()}`;
            const groupConfig = {
                key: gruopKey,
                buttons: [],
            };
            if (Array.isArray(group)) {
                // 简写的 schema 形态，进行补全
                groupConfig.buttons = group;
            }
            else if (typeof group === 'string') {
                groupConfig.buttons.push([group]);
            }
            else {
                return Object.assign(Object.assign(Object.assign({}, groupConfig), { 
                    // 如果配置了 shelter 但未配置 shrinkType，则默认为 OnTheSpot 模式
                    shrinkType: group.shelter ? ShrinkType.ON_THE_SPOT : undefined }), group);
            }
            return groupConfig;
        });
    }
    getTakeUpSpaceHeight() {
        const hoverConfig = this.store.getValue(WorkbenchConfigurationState.TitlebarHoverable);
        const titlebarHeight = this.store.getValue('titlebar.height');
        if (!hoverConfig || typeof hoverConfig === 'boolean' || !hoverConfig.takeUpSpace) {
            return 0;
        }
        if (!hoverConfig.takeUpSpaceHeight) {
            return titlebarHeight / 3;
        }
        return hoverConfig.takeUpSpaceHeight;
    }
    /**
     * 获取某个特定 config
     *
     * @internal
     *
     * @param key - config 的 name
     */
    get(key) {
        return super.get(key, {
            name: 'none',
            tooltip: '',
            onClick() {
                // do nothing
            },
            shouldHide() { return true; },
        });
    }
    $forceUpdate() {
        this.force += 1;
        this.onUpdate();
    }
    createTailShelter(item, group) {
        const currentSchema = this.schema[this.currentPath[0]];
        const shelterConfig = item.shelter
            ? this.get(item.shelter)
            : {
                name: `${group.key}-tail-shelter`,
                icon: DEFAULT_SHELTER_ICON,
                hasArrow: false,
                tooltip: currentSchema.moreTooltip,
                text: currentSchema.moreText,
                dropdownContent: getShelterDropdown(`${group.key}-tail-shelter`),
            };
        this.setPathToActionInfoInConfig(shelterConfig, [...this.currentPath]);
        return {
            key: `VG_tail_shelter_${generateRandomId()}`,
            shelter: null,
            priority: 999,
            buttons: [[shelterConfig]],
        };
    }
    /**
     * @beta
     * 该功能目前为实验性功能，用法和效果可能会在未来的版本中进行调整
     *
     * 设置 Ribbon 工具栏的激活 tab，若当前工具栏不是 Ribbon 工具栏，则会在下次
     * 切换到 Ribbon 工具栏时自动激活该 tab
     */
    changeRibbonTab(key) {
        this.store.update(BuiltinUiState.RibbonActiveTab, key);
    }
};
ToolbarService = __decorate([
    Service({ overridable: true, scope: 'ui:service', name: ServiceNames.TOOLBAR }),
    __metadata("design:paramtypes", [StateManager, TransitionService])
], ToolbarService);

export { ToolbarService };
//# sourceMappingURL=toolbar-service.js.map
